/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

/**
 * Override this widget to force the zone attached under each column in the grid to fill its parent height,
 * This will help us aligning grid content vertically across multiple columns.
 * Also, enhance the responsivity of this grid.
 */

import { Container } from "@eisinc/App/Components/Container/Container";
import { screen } from "@eisinc/XDL/Helpers";
import systemColors from "@eisinc/XDL/PreStyle/colors";
import { color } from "@eisinc/XDL/Utilities";
import { getAllColorsAsDropdownOptions } from "@eisinc/XDL/Utilities/getAllColorsAsDropdownOptions";
import isNumeric from "@insite/client-framework/Common/isNumeric";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import Zone from "@insite/client-framework/Components/Zone";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
    border = "border",
    borderSize = "borderSize",
    padding = "padding",
    paddingSize = "paddingSize",
    breakLine = "breakLine",
    fullWidth = "fullWidth",
    fluid = "fluid",
    bgColor = "bgColor",
    margin = "margin",
    marginSize = "marginSize",
}

interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.border]: boolean;
        [fields.borderSize]: number;
        [fields.padding]: boolean;
        [fields.paddingSize]: string;
        [fields.breakLine]: boolean;
        [fields.fullWidth]: boolean;
        [fields.fluid]: boolean;
        [fields.bgColor]: string;
        [fields.margin]: boolean;
        [fields.marginSize]: string;
    };
}

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [3, 3, 1, 1, 1],
    2: [4, 4, 2, 2, 2],
    3: [12, 6, 6, 3, 3], // <xng-change on xs let the column fill all the available width />
    4: [12, 6, 4, 4, 4], // <xng-change on xs let the column fill all the available width />
    5: [12, 12, 5, 5, 5],
    6: [12, 12, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

const breakLineStyle = (gap: number) => {
    return css`
        padding-right: ${gap || 0}px;
        position: relative;
        ${screen("lte", "sm")`
            margin-bottom: 20px !important;
        `}

        &:not(:last-child):after {
            content: " ";
            border-right: 2px solid ${color("common.border", 0.6)};
            height: 100%;
            ${screen("lte", "sm")`
                border-right: none;
                border-bottom: 2px solid ${color("common.border", 0.6)};
                width: 100%;
                position: absolute;
                top: 15px;
            `}
        }
    `;
};

// <xng-change force the zone inside the grid item to fill its parent height>
const wrapperCss = css`
    & > div {
        height: 100%;
    }
`;
// <xng-change>

const Row: FC<Props> = (props: Props) => {
    const {
        fields: {
            columns,
            gap,
            alignment,
            reflow,
            border,
            borderSize,
            padding,
            paddingSize,
            breakLine,
            fullWidth,
            fluid,
            bgColor,
            margin,
            marginSize,
        },
        id,
    } = props;

    const columnElements: JSX.Element[] = [];

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";

        columnElements.push(
            <GridItem
                key={x}
                width={reflow ? convertToResponsive(columns[x]) : columns[x]}
                align={align}
                css={breakLine ? breakLineStyle(gap || 0) : align === "top" ? wrapperCss : undefined}
            >
                <Zone contentId={id} zoneName={`Content${x}`} />
            </GridItem>,
        );
    }

    const getSize = (size: string) => {
        if (!size) {
            return null;
        }

        if (size.toString().includes(",")) {
            let values = "";

            size.split(",").forEach(item => {
                values += `${item.trim()}px `;
            });

            return values;
        }

        return `${size}px`;
    };

    return (
        <Container
            fullWidth={fullWidth}
            background={bgColor}
            fluid={fluid}
            // xng-change: removing horizental scroll and make the items inside aligned 
            padding={!fullWidth ? [10, 10, 10, 10, 10] : [0, 0, 0, 0, 0]}
            css={css`
                margin: ${margin ? getSize(marginSize) : "0 auto"};
                max-width: ${fullWidth ? "100%" : ""};
                & > div {
                    margin: ${!fullWidth ? `0 auto;` : 0};    
                    ${!fullWidth ? `margin: 0 auto;` : ""}
                    ${fullWidth && !fluid ? "max-width: 100%;" : ""}
            `}
        >
            <GridContainer
                gap={gap || 0}
                css={css`
                    border-color: ${color("common.border", 0.6)};
                    border-style: solid;
                    border-radius: 3px;
                    border-width: ${border ? `${borderSize}px` : 0};
                    padding: ${padding ? getSize(paddingSize) : 0};
                `}
            >
                {columnElements}
            </GridContainer>
        </Container>
    );
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const customTab = {
    displayName: "Customization",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Row,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 10,
            },
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
            {
                name: fields.border,
                displayName: "Border",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.borderSize,
                displayName: "Border Size",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                max: 1000,
                min: 1,
                tab: customTab,
                isVisible: item => item.fields.border,
            },
            {
                name: fields.padding,
                displayName: "Padding",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.paddingSize,
                displayName: "Padding Size",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "20",
                isRequired: true,
                placeholder: "Enter: 5 or '5, 6' or '5, 6, 7, 8'",
                tab: customTab,
                validate: (value: string | null, item: HasFields) => {
                    if (value && value.split(",").length > 4) {
                        return `Invalid Input`;
                    }
                    if (value && isNumeric(value) && parseFloat(value) < 0) {
                        return `Invalid Input`;
                    }
                    return null;
                },
                isVisible: item => item.fields.padding,
            },
            {
                name: fields.breakLine,
                displayName: "Vertical Lines between items",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.fullWidth,
                displayName: "Full Width",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.fluid,
                displayName: "Fluid Container",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: customTab,
                isVisible: item => item.fields.fullWidth,
            },
            {
                name: fields.bgColor,
                displayName: "Background Color",
                editorTemplate: "DropDownField",
                options: getAllColorsAsDropdownOptions(systemColors),
                defaultValue: "",
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.margin,
                displayName: "Margin",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customTab,
            },
            {
                name: fields.marginSize,
                displayName: "Margin Size",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "10",
                isRequired: true,
                placeholder: "Enter: 5 or '5, 6' or '5, 6, 7, 8'",
                tab: customTab,
                validate: (value: string | null, item: HasFields) => {
                    if (value && value.split(",").length > 4) {
                        return `Invalid Input`;
                    }
                    if (value && isNumeric(value) && parseFloat(value) < 0) {
                        return `Invalid Input`;
                    }
                    return null;
                },
                isVisible: item => item.fields.margin,
            },
        ],
    },
};

export default widgetModule;
